import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, BACKEND_URL } from "@/lib/api";
import { formatAuDate } from "@/lib/date";
import { Loader2, Calendar, ArrowRight } from "lucide-react";

const dayLabel = (iso) => formatAuDate(iso);

export default function EventLanding() {
  const { slug } = useParams();
  const [ev, setEv] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get(`/public/events/${slug}`).then((r) => setEv(r.data)).catch(() => setError(true));
  }, [slug]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white p-6">
        <div className="max-w-md text-center border border-black/10 p-10">
          <h1 className="font-display text-2xl font-black">Event not available</h1>
          <p className="mt-2 text-sm text-zinc-600">This event is either private, unpublished or doesn't exist.</p>
          <Link to="/" className="mt-6 inline-block brand-btn px-4 py-2 text-sm">VOLMEE Home</Link>
        </div>
      </div>
    );
  }
  if (!ev) {
    return <div className="min-h-screen flex items-center justify-center text-zinc-500"><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading event…</div>;
  }

  const coverUrl = ev.cover_image ? `${BACKEND_URL}/api/public/events/${slug}/cover-image` : null;

  return (
    <div className="min-h-screen bg-white">
      <header className="sticky top-0 z-30 border-b border-black/10 bg-white/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-6 py-4">
          <Link to="/" className="font-display text-xl font-black tracking-tighter">VOLMEE<span className="text-[#FF3B30]">.</span></Link>
          <div className="flex items-center gap-3 text-sm">
            <Link to={`/events/${slug}/vacancies`} className="text-zinc-600 hover:text-black" data-testid="event-vacancies-link">Vacancies</Link>
            <Link to={`/events/${slug}/register`} data-testid="event-register-cta" className="brand-btn inline-flex items-center gap-2 px-4 py-2 text-sm font-medium">
              Register <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </header>

      {coverUrl ? (
        <section className="w-full bg-black" data-testid="event-cover-banner">
          <div className="mx-auto max-w-5xl">
            <img
              src={coverUrl}
              alt={`${ev.name} cover`}
              className="block w-full max-h-[520px] object-cover"
              onError={(e) => { e.currentTarget.parentElement.parentElement.style.display = "none"; }}
              data-testid="event-cover-image"
            />
          </div>
        </section>
      ) : null}

      <section className="border-b border-black/10 bg-[#F4F4F5]">
        <div className="mx-auto max-w-5xl px-6 py-16 md:py-20">
          <div className="inline-flex items-center gap-2 border border-black/20 bg-white px-3 py-1 text-[10px] uppercase tracking-widest">
            <Calendar className="h-3 w-3" /> Volunteer registration open
          </div>
          <h1 className="mt-4 font-display text-5xl font-black leading-[0.95] tracking-tighter md:text-7xl" data-testid="event-title">{ev.name}</h1>
          {ev.description ? <p className="mt-4 max-w-2xl text-base text-zinc-700 md:text-lg" data-testid="event-description">{ev.description}</p> : null}
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to={`/events/${slug}/register`} data-testid="event-register-hero" className="brand-btn inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold">
              Register as a volunteer <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to={`/events/${slug}/vacancies`} className="inline-flex items-center gap-2 border border-black px-6 py-3 text-sm font-semibold hover:bg-black hover:text-white transition-colors">
              See what's still open
            </Link>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-6 py-14">
        <div className="text-xs uppercase tracking-widest text-[#002FA7]">Event days</div>
        <h2 className="mt-1 font-display text-3xl font-black tracking-tighter">What's on, day by day</h2>
        <div className="mt-6 grid gap-6 md:grid-cols-2">
          {(ev.days || []).map((d) => (
            <div key={d.date} data-testid={`event-day-${d.date}`} className="border border-black/10 p-6">
              <div className="font-display text-xl font-black tracking-tighter">{dayLabel(d.date)}</div>
              <div className="mt-4">
                <div className="text-xs uppercase tracking-widest text-zinc-500">Sessions & roles</div>
                <ul className="mt-2 space-y-3 text-sm">
                  {(d.sessions || []).map((s) => {
                    const name = typeof s === "string" ? s : s.name;
                    const start = typeof s === "string" ? "" : (s.start || "");
                    const end = typeof s === "string" ? "" : (s.end || "");
                    const location = typeof s === "string" ? "" : ((s.location || "").trim());
                    const mapsHref = location ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(location)}` : "";
                    const roles = (typeof s === "string" ? [] : s.roles) || [];
                    return (
                      <li key={name}>
                        <div><span className="font-semibold">{name}</span>{start || end ? <span className="ml-2 font-mono text-zinc-500 text-xs">{start || "?"} – {end || "?"}</span> : null}</div>
                        {location ? (
                          <div className="mt-0.5">
                            <a href={mapsHref} target="_blank" rel="noreferrer" className="font-mono text-xs text-[#002FA7] underline decoration-dotted hover:decoration-solid" title="Open in Google Maps">📍 {location}</a>
                          </div>
                        ) : null}
                        <ul className="mt-1 flex flex-wrap gap-1.5">
                          {roles.map((r) => <li key={r} className="border border-black/10 bg-white px-2 py-0.5 text-[11px]">{r}</li>)}
                          {!roles.length ? <li className="italic text-zinc-400 text-xs">No roles set</li> : null}
                        </ul>
                      </li>
                    );
                  })}
                  {!(d.sessions || []).length ? <li className="italic text-zinc-400">No sessions set for this day.</li> : null}
                </ul>
              </div>
              {null}
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
